// src/components/auth/OAuthCallback.tsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { socialAuthService } from '../../services/socialAuthService';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Alert } from '../ui/alert';
import { SocialProvider, SocialAuthCredentials } from '../../types/auth';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { socialAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const isProcessed = useRef<boolean>(false);

  useEffect(() => {
    // Prevent multiple executions of the callback handler
    if (isProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      isProcessed.current = true;
      
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      // Fix the provider extraction by checking for path segments or falling back to the search params
      let provider: SocialProvider | null = null;
      
      // Extract from path (/auth/google/callback -> 'google')
      const pathSegments = location.pathname.split('/');
      if (pathSegments.length >= 3) {
        // Check if the second-to-last segment is a valid provider
        const pathProvider = pathSegments[pathSegments.length - 2];
        if (['github', 'google', 'linkedin'].includes(pathProvider)) {
          provider = pathProvider as SocialProvider;
        } else {
          // Try the last segment
          const lastSegment = pathSegments[pathSegments.length - 1];
          if (['github', 'google', 'linkedin'].includes(lastSegment)) {
            provider = lastSegment as SocialProvider;
          }
        }
      }
      
      // If provider is still null, try to get it from search params
      if (!provider) {
        const searchProvider = params.get('provider');
        if (searchProvider && ['github', 'google', 'linkedin'].includes(searchProvider)) {
          provider = searchProvider as SocialProvider;
        }
      }

      if (!code || !state || !provider) {
        setError('Invalid callback parameters. Missing code, state, or provider.');
        console.error('Missing parameters:', { code: !!code, state: !!state, provider, path: location.pathname });
        return;
      }

      try {
        const credentials: SocialAuthCredentials = {
          provider,
          code,
          state
        };

        console.log('Processing OAuth callback with credentials:', { provider, hasCode: !!code, hasState: !!state });

        // Validate the callback first
        await socialAuthService.validateCallback(credentials);
        
        // Then process the social authentication
        await socialAuth(credentials);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
        // Clear the URL parameters to prevent retry loops
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [location.search, location.pathname, navigate, socialAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" message="Completing authentication..." />
    </div>
  );
};

export default OAuthCallback;
